import React, { useEffect, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Field } from '@components/framework/formfield/Field.tsx';
import { formatCoordinates } from '@utils/utils.tsx';

export interface LocationCoordinatesValue {
  latitude?: number | null;
  longitude?: number | null;
}

interface ComponentProps {
  secured?: SecuredConfig;
  value: LocationCoordinatesValue;
  onChange: (value: LocationCoordinatesValue) => void;
  icon?: React.ReactElement;
  readOnly?: boolean;
  dataTestId?: string;
}

/**
 * HERE-resolved GPS position of a company location with manual override. Saving requires both values;
 * the backend keeps recalculating from the address until the user overrides them here.
 */
const LocationCoordinatesTag: React.FC<ComponentProps> = ({ secured, value, onChange, icon, readOnly, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<LocationCoordinatesValue>(value);

  useEffect(() => setInternalValue(value), [value.latitude, value.longitude]);

  const bothFilled = internalValue.latitude != null && internalValue.longitude != null;

  const pane = (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <Field label={tr('LocalityDetail.latitude', 'Latitude')} width="100%">
          <InputNumber
            className="w-full"
            inputClassName="w-full"
            value={internalValue.latitude ?? null}
            onValueChange={e => setInternalValue(v => ({ ...v, latitude: e.value ?? null }))}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={7}
            useGrouping={false}
            min={-90}
            max={90}
            data-testid={dataTestId ? `${dataTestId}-latitude` : undefined}
          />
        </Field>
        <Field label={tr('LocalityDetail.longitude', 'Longitude')} width="100%">
          <InputNumber
            className="w-full"
            inputClassName="w-full"
            value={internalValue.longitude ?? null}
            onValueChange={e => setInternalValue(v => ({ ...v, longitude: e.value ?? null }))}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={7}
            useGrouping={false}
            min={-180}
            max={180}
            data-testid={dataTestId ? `${dataTestId}-longitude` : undefined}
          />
        </Field>
      </div>
      {!bothFilled && <div className="text-xs text-content-secondary">{tr('LocalityDetail.coordinatesBothRequired', 'Fill in both latitude and longitude.')}</div>}
    </div>
  );

  return (
    <ButtonTag
      secured={secured}
      label={tr('LocalityDetail.coordinates', 'GPS (lat/lng)')}
      icon={icon}
      value={formatCoordinates(value.latitude, value.longitude) ?? '–'}
      actionIcon={<SvgTagEdit />}
      actionPane={pane}
      popupWidth={'420px'}
      onSave={onSuccess => {
        if (!bothFilled) return;
        onChange({ latitude: internalValue.latitude, longitude: internalValue.longitude });
        onSuccess();
      }}
      readonly={readOnly}
      autoClose
      dataTestId={dataTestId}
    />
  );
};

export default LocationCoordinatesTag;
